import React from 'react'

import { useDashboardFeatureFlags } from '@components/utils/DashboardFeatureFlags'
import { Checkbox } from '@fourthwall/components'
import { useFormik } from '@fourthwall/utils/dist/formik'

import { FilterDropdown } from '../../../../../../../components/common/FilterDropdown'
import * as S from './styled'
import type { FilterDropdownViewProps, FilterFormValues, FilterType } from './types'

export const MembersFilterView = ({
  types,
  tiers,
  billingCycles,
  states,
  initialValues,
  filterActive,
  onSubmit,
  onReset,
}: FilterDropdownViewProps) => {
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()

  const formik = useFormik<FilterFormValues>({
    initialValues,
    onSubmit: () => {
      if (isDisabled()) {
        onSubmit({ ...formik.values, tier: [], billingCycle: [] })

        return
      }

      onSubmit(formik.values)
    },
    enableReinitialize: true,
  })

  const handleReset = () => {
    formik.resetForm()
    onReset()
  }

  const handleCheckboxGroupChange = (checked: boolean, value: string, name: string) => {
    const fieldValue = checked
      ? [...(formik.values as any)[name], value]
      : (formik.values as any)[name].filter((v: any) => v !== value)

    formik.setFieldValue(name, fieldValue)
  }

  const isDisabled = () => {
    const type = formik.values.type as unknown as FilterType[]

    return type.length === 1 && type[0] === 'free'
  }

  const renderTiers = () =>
    !!tiers?.length && (
      <S.FiltersGroup>
        <FilterDropdown.GroupTitle title="Membership tier" />
        <S.CheckboxGroup role="group">
          {tiers.map((option) => (
            <Checkbox
              native
              key={option.value}
              label={option.label}
              value={option.value}
              checked={formik.values.tier.includes(option.value)}
              disabled={isDisabled()}
              onValueChange={(checked) => handleCheckboxGroupChange(checked, option.value, 'tier')}
            />
          ))}
        </S.CheckboxGroup>
      </S.FiltersGroup>
    )

  const renderBillingCycles = () =>
    !!billingCycles?.length && (
      <S.FiltersGroup>
        <FilterDropdown.GroupTitle title="Membership billing cycle" />
        <S.CheckboxGroup role="group">
          {billingCycles!.map((option) => (
            <Checkbox
              native
              key={option.value}
              label={option.label}
              value={option.value}
              checked={formik.values.billingCycle.includes(option.value)}
              disabled={isDisabled()}
              onValueChange={(checked) =>
                handleCheckboxGroupChange(checked, option.value, 'billingCycle')
              }
            />
          ))}
        </S.CheckboxGroup>
      </S.FiltersGroup>
    )

  const renderStates = () =>
    !!states?.length && (
      <S.FiltersGroup>
        <FilterDropdown.GroupTitle title="Membership state" />
        <S.CheckboxGroup role="group">
          {states!.map((option) => (
            <Checkbox
              native
              key={option.value}
              label={option.label}
              value={option.value}
              checked={formik.values.state.includes(option.value)}
              disabled={isDisabled()}
              onValueChange={(checked) => handleCheckboxGroupChange(checked, option.value, 'state')}
            />
          ))}
          {dashboardFeatureFlags.membershipsGifting.enabled && (
            <Checkbox
              native
              label="Gifted by others"
              value="giftedByOthers"
              checked={formik.values.giftedByOthers}
              onValueChange={(checked) => formik.setFieldValue('giftedByOthers', checked)}
            />
          )}
        </S.CheckboxGroup>
      </S.FiltersGroup>
    )

  const renderTypes = () =>
    !!types?.length && (
      <S.FiltersGroup>
        <FilterDropdown.GroupTitle title="Type" />
        <S.CheckboxGroup role="group">
          {types!.map((option) => (
            <Checkbox
              native
              key={option.value}
              label={option.label}
              value={option.value}
              checked={formik.values.type.includes(option.value)}
              onValueChange={(checked) => handleCheckboxGroupChange(checked, option.value, 'type')}
            />
          ))}
        </S.CheckboxGroup>
      </S.FiltersGroup>
    )

  return (
    <FilterDropdown
      content={
        <S.Form>
          <div>
            {renderTypes()}
            {renderStates()}
          </div>
          <div>
            {renderBillingCycles()}
            {renderTiers()}
          </div>
        </S.Form>
      }
      activityIndicator={filterActive}
      onSubmit={formik.handleSubmit}
      onReset={handleReset}
    />
  )
}
